/**
 * Finds the plots sitting on the outside of a block, so calibration can warn a
 * grower that the reference tree they picked is an edge tree.
 *
 * The heuristic, per plot: take the bearing to every plot within a neighbour
 * radius, sort the bearings, and measure the largest angular gap between
 * consecutive ones. An interior plot is surrounded, so its widest gap stays
 * small. A plot on the outside has open ground on one side, so its widest gap
 * is large. A gap of EDGE_ANGLE_GAP_DEGREES or more means edge, as does having
 * fewer than MIN_NEIGHBOURS_FOR_INTERIOR neighbours at all.
 *
 * The radius is derived from the block itself rather than from the field
 * properties, which are frequently unset. The median nearest-neighbour distance
 * is the planting distance (spacing along a row); the median distance to the
 * nearest plot more than 45 degrees off that direction is the row width. The
 * radius is NEIGHBOUR_RADIUS_FACTOR row widths: far enough to reach the
 * adjacent rows, not so far that it reaches past them.
 *
 * Assumptions:
 * - Lon/lat is flattened to metres by an equirectangular projection about the
 *   block centroid (METRES_PER_DEGREE with a cos(lat) correction on x). Over a
 *   single block the distortion is negligible; this would not hold over an
 *   extent of hundreds of kilometres.
 * - Row width is roughly constant across the block. Where it varies a lot, the
 *   radius fits the median and plots in the wider-spaced parts can read as edge.
 * - Blocks below MIN_PLOTS_FOR_INTERIOR plots, and single-row blocks, have no
 *   meaningful interior and are reported as entirely edge. findWarnableEdgePlots
 *   suppresses that case, so those blocks produce no warning.
 *
 * Neighbour lookups go through a uniform grid index rather than comparing every
 * pair of plots. Real fields reach 40k+ plots, where the pairwise version costs
 * minutes of frozen page and the indexed one costs under a second.
 */
import { centerOfMass } from "@turf/turf";
import { AppleCountCalibrationFeatureCollection } from "../Calibration.types";

export const EDGE_ANGLE_GAP_DEGREES = 120;
export const NEIGHBOUR_RADIUS_FACTOR = 1.5;
export const MIN_PLOTS_FOR_INTERIOR = 9;
export const MIN_NEIGHBOURS_FOR_INTERIOR = 3;

const METRES_PER_DEGREE = 111320;
const ACROSS_ROW_MAX_COS = Math.SQRT1_2;
const ACROSS_ROW_SEARCH_FACTOR = 10;
const SINGLE_ROW_RATIO = 0.5;

type Origin = { lon: number; lat: number };

type LocalPoint = { x: number; y: number };

type PlotPoint = LocalPoint & { plotId: number };

type Grid = {
  cellSize: number;
  minX: number;
  minY: number;
  columns: number;
  rows: number;
  cells: Array<number[] | undefined>;
  points: LocalPoint[];
};

function toLocalMetres(lon: number, lat: number, origin: Origin): LocalPoint {
  const cosLat = Math.cos((origin.lat * Math.PI) / 180);
  return {
    x: (lon - origin.lon) * cosLat * METRES_PER_DEGREE,
    y: (lat - origin.lat) * METRES_PER_DEGREE,
  };
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
}

function cellIndex(value: number, minimum: number, cellSize: number): number {
  return Math.floor((value - minimum) / cellSize);
}

function buildGrid(points: LocalPoint[], cellSize: number): Grid {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const columns = Math.max(1, cellIndex(Math.max(...xs), minX, cellSize) + 1);
  const rows = Math.max(1, cellIndex(Math.max(...ys), minY, cellSize) + 1);
  const cells: Array<number[] | undefined> = new Array(columns * rows);

  points.forEach((point, index) => {
    const column = Math.min(columns - 1, cellIndex(point.x, minX, cellSize));
    const row = Math.min(rows - 1, cellIndex(point.y, minY, cellSize));
    const key = row * columns + column;
    const cell = cells[key];
    cells[key] = cell ? [...cell, index] : [index];
  });

  return { cellSize, minX, minY, columns, rows, cells, points };
}

function visitCell(
  grid: Grid,
  column: number,
  row: number,
  visit: (index: number) => void,
): void {
  if (column < 0 || column >= grid.columns || row < 0 || row >= grid.rows) {
    return;
  }
  const cell = grid.cells[row * grid.columns + column];
  if (!cell) return;
  cell.forEach(visit);
}

function forEachInRing(
  grid: Grid,
  x: number,
  y: number,
  ring: number,
  visit: (index: number) => void,
): void {
  const centreColumn = cellIndex(x, grid.minX, grid.cellSize);
  const centreRow = cellIndex(y, grid.minY, grid.cellSize);
  const minColumn = centreColumn - ring;
  const maxColumn = centreColumn + ring;
  const minRow = centreRow - ring;
  const maxRow = centreRow + ring;

  for (let column = minColumn; column <= maxColumn; column++) {
    visitCell(grid, column, minRow, visit);
    if (maxRow !== minRow) visitCell(grid, column, maxRow, visit);
  }
  for (let row = minRow + 1; row <= maxRow - 1; row++) {
    visitCell(grid, minColumn, row, visit);
    visitCell(grid, maxColumn, row, visit);
  }
}

function forEachWithinRadius(
  grid: Grid,
  x: number,
  y: number,
  radius: number,
  visit: (index: number) => void,
): void {
  const minColumn = cellIndex(x - radius, grid.minX, grid.cellSize);
  const maxColumn = cellIndex(x + radius, grid.minX, grid.cellSize);
  const minRow = cellIndex(y - radius, grid.minY, grid.cellSize);
  const maxRow = cellIndex(y + radius, grid.minY, grid.cellSize);

  for (let row = minRow; row <= maxRow; row++) {
    for (let column = minColumn; column <= maxColumn; column++) {
      visitCell(grid, column, row, visit);
    }
  }
}

function findNearest(
  grid: Grid,
  index: number,
  accept: (candidate: number, distance: number) => boolean,
): { index: number; distance: number } {
  const origin = grid.points[index];
  const maxRing = Math.max(grid.columns, grid.rows);
  let bestIndex = -1;
  let bestDistance = Infinity;

  for (let ring = 0; ring <= maxRing; ring++) {
    const minPossible = (ring - 1) * grid.cellSize;
    if (bestIndex >= 0 && minPossible > bestDistance) break;

    forEachInRing(grid, origin.x, origin.y, ring, (candidate) => {
      if (candidate === index) return;
      const other = grid.points[candidate];
      const distance = Math.hypot(other.x - origin.x, other.y - origin.y);
      if (distance <= 0 || distance >= bestDistance) return;
      if (!accept(candidate, distance)) return;
      bestIndex = candidate;
      bestDistance = distance;
    });
  }

  return { index: bestIndex, distance: bestDistance };
}

function isFinitePosition(position: unknown): boolean {
  return (
    Array.isArray(position) &&
    position.length >= 2 &&
    Number.isFinite(position[0]) &&
    Number.isFinite(position[1])
  );
}

function readPlotPoints(
  geojson: AppleCountCalibrationFeatureCollection,
): Array<{ plotId: number; lon: number; lat: number }> {
  return (geojson?.features ?? []).flatMap((feature) => {
    const plotId = feature?.properties?.plot_id;
    if (!Number.isFinite(plotId)) return [];
    const ring = feature?.geometry?.coordinates?.[0];
    if (!Array.isArray(ring) || ring.length < 3) return [];
    if (!ring.every(isFinitePosition)) return [];

    const [lon, lat] = centerOfMass(feature).geometry.coordinates;

    return [{ plotId, lon, lat }];
  });
}

function estimateBlockSpacing(grid: Grid): {
  plantingDistanceM: number;
  rowWidthM: number;
  isSingleRow: boolean;
} {
  const plantingDistances: number[] = [];
  const rowWidths: number[] = [];

  grid.points.forEach((point, index) => {
    const nearest = findNearest(grid, index, () => true);
    if (nearest.index < 0) return;
    plantingDistances.push(nearest.distance);

    const alongRow = grid.points[nearest.index];
    const alongRowX = (alongRow.x - point.x) / nearest.distance;
    const alongRowY = (alongRow.y - point.y) / nearest.distance;
    const limit = nearest.distance * ACROSS_ROW_SEARCH_FACTOR;

    const acrossRow = findNearest(grid, index, (candidate, distance) => {
      if (distance > limit) return false;
      const other = grid.points[candidate];
      const unitX = (other.x - point.x) / distance;
      const unitY = (other.y - point.y) / distance;
      return (
        Math.abs(unitX * alongRowX + unitY * alongRowY) < ACROSS_ROW_MAX_COS
      );
    });

    if (acrossRow.index >= 0) rowWidths.push(acrossRow.distance);
  });

  if (plantingDistances.length === 0) {
    return { plantingDistanceM: 0, rowWidthM: 0, isSingleRow: true };
  }

  const plantingDistanceM = median(plantingDistances);
  const isSingleRow = rowWidths.length < grid.points.length * SINGLE_ROW_RATIO;

  return {
    plantingDistanceM,
    rowWidthM: isSingleRow ? plantingDistanceM : median(rowWidths),
    isSingleRow,
  };
}

function largestAngularGap(angles: number[]): number {
  const sorted = [...angles].sort((a, b) => a - b);

  return sorted.reduce((largest, angle, index) => {
    const next =
      index === sorted.length - 1 ? sorted[0] + 2 * Math.PI : sorted[index + 1];
    return Math.max(largest, next - angle);
  }, 0);
}

function classifyPlots(geojson: AppleCountCalibrationFeatureCollection): {
  edgePlotIds: Set<number>;
  plotCount: number;
} {
  const raw = readPlotPoints(geojson);
  if (raw.length === 0) return { edgePlotIds: new Set(), plotCount: 0 };

  const origin = {
    lon: raw.reduce((total, point) => total + point.lon, 0) / raw.length,
    lat: raw.reduce((total, point) => total + point.lat, 0) / raw.length,
  };

  const points: PlotPoint[] = raw.map((point) => ({
    plotId: point.plotId,
    ...toLocalMetres(point.lon, point.lat, origin),
  }));

  const spread = Math.max(
    Math.max(...points.map((point) => point.x)) -
      Math.min(...points.map((point) => point.x)),
    Math.max(...points.map((point) => point.y)) -
      Math.min(...points.map((point) => point.y)),
  );
  const bootstrapCell = Math.max(
    1,
    (2 * spread) / Math.sqrt(Math.max(1, points.length)),
  );

  const { plantingDistanceM, rowWidthM, isSingleRow } = estimateBlockSpacing(
    buildGrid(points, bootstrapCell),
  );

  if (plantingDistanceM <= 0) {
    return { edgePlotIds: new Set(), plotCount: points.length };
  }

  const everyPlot = new Set(points.map((point) => point.plotId));
  if (isSingleRow || points.length < MIN_PLOTS_FOR_INTERIOR) {
    return { edgePlotIds: everyPlot, plotCount: points.length };
  }

  const radius = NEIGHBOUR_RADIUS_FACTOR * rowWidthM;
  const grid = buildGrid(points, radius);
  const gapThreshold = (EDGE_ANGLE_GAP_DEGREES * Math.PI) / 180;
  const edgePlots = new Set<number>();

  points.forEach((point, index) => {
    const angles: number[] = [];
    forEachWithinRadius(grid, point.x, point.y, radius, (candidate) => {
      if (candidate === index) return;
      const other = points[candidate];
      const deltaX = other.x - point.x;
      const deltaY = other.y - point.y;
      if (Math.hypot(deltaX, deltaY) > radius) return;
      angles.push(Math.atan2(deltaY, deltaX));
    });

    const isEdge =
      angles.length < MIN_NEIGHBOURS_FOR_INTERIOR ||
      largestAngularGap(angles) >= gapThreshold;

    if (isEdge) edgePlots.add(point.plotId);
  });

  return { edgePlotIds: edgePlots, plotCount: points.length };
}

export function findEdgePlots(
  geojson: AppleCountCalibrationFeatureCollection,
): Set<number> {
  return classifyPlots(geojson).edgePlotIds;
}

export function findWarnableEdgePlots(
  geojson: AppleCountCalibrationFeatureCollection,
): Set<number> {
  const { edgePlotIds, plotCount } = classifyPlots(geojson);

  return edgePlotIds.size === plotCount ? new Set() : edgePlotIds;
}
